import { motion, useReducedMotion } from 'framer-motion';
import { EASE, fadeIn, fadeUpItem, staggerParent } from '@/animations/variants';
import { Reveal } from '@/components/ui/Reveal';
import { salonInfo } from '@/mocks/salonData';

export default function AccessSection() {
  const reduce = useReducedMotion();

  return (
    <section
      id="access"
      className="py-20 sm:py-28 lg:py-36 bg-cream"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <Reveal className="mb-12 sm:mb-16">
          <p className="section-label mb-4">SALON & ACCESS</p>
          <h2 className="font-serif-jp text-deep-brown text-xl sm:text-2xl lg:text-3xl leading-snug">
            サロン案内・アクセス
          </h2>
        </Reveal>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
          {/* Info */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <h3 className="font-serif-jp text-deep-brown font-medium mb-1 text-lg">ほぐし屋さん</h3>
              <p className="font-sans-jp text-earth text-sm">Hogushiya Luminosite</p>
            </div>

            <Reveal className="space-y-5" variants={staggerParent}>
              <motion.div className="flex items-start gap-4" variants={fadeUpItem}>
                <motion.div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-forest/10 border border-forest/20 hover:border-forest/30 transition-colors duration-200"
                  whileHover={reduce ? undefined : { scale: 1.05 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <i className="ri-map-pin-line text-forest text-lg"></i>
                </motion.div>
                <div>
                  <p className="font-sans-jp text-earth/70 text-xs mb-0.5">住所</p>
                  <p className="font-sans-jp text-deep-brown text-sm">{salonInfo.address}</p>
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={fadeUpItem}>
                <motion.div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-forest/10 border border-forest/20 hover:border-forest/30 transition-colors duration-200"
                  whileHover={reduce ? undefined : { scale: 1.05 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <i className="ri-phone-line text-forest text-lg"></i>
                </motion.div>
                <div>
                  <p className="font-sans-jp text-earth/70 text-xs mb-0.5">電話番号</p>
                  <p className="font-sans-jp text-deep-brown text-sm">{salonInfo.phone1}</p>
                  <p className="font-sans-jp text-deep-brown text-sm">{salonInfo.phone2}</p>
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={fadeUpItem}>
                <motion.div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-forest/10 border border-forest/20 hover:border-forest/30 transition-colors duration-200"
                  whileHover={reduce ? undefined : { scale: 1.05 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <i className="ri-time-line text-forest text-lg"></i>
                </motion.div>
                <div>
                  <p className="font-sans-jp text-earth/70 text-xs mb-0.5">営業時間</p>
                  <p className="font-sans-jp text-deep-brown text-sm">{salonInfo.hours}</p>
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={fadeUpItem}>
                <motion.div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-forest/10 border border-forest/20 hover:border-forest/30 transition-colors duration-200"
                  whileHover={reduce ? undefined : { scale: 1.05 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <i className="ri-calendar-line text-forest text-lg"></i>
                </motion.div>
                <div>
                  <p className="font-sans-jp text-earth/70 text-xs mb-0.5">定休日</p>
                  <p className="font-sans-jp text-deep-brown text-sm">{salonInfo.holiday}</p>
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={fadeUpItem}>
                <motion.div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-forest/10 border border-forest/20 hover:border-forest/30 transition-colors duration-200"
                  whileHover={reduce ? undefined : { scale: 1.05 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <i className="ri-walk-line text-forest text-lg"></i>
                </motion.div>
                <div>
                  <p className="font-sans-jp text-earth/70 text-xs mb-0.5">アクセス</p>
                  {salonInfo.stations.map((station, index) => (
                    <p key={index} className="font-sans-jp text-deep-brown text-sm">
                      {station.line} {station.walk}
                    </p>
                  ))}
                </div>
              </motion.div>

              <motion.div className="flex items-start gap-4" variants={fadeUpItem}>
                <motion.div
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-forest/10 border border-forest/20 hover:border-forest/30 transition-colors duration-200"
                  whileHover={reduce ? undefined : { scale: 1.05 }}
                  transition={{ duration: 0.25, ease: EASE }}
                >
                  <i className="ri-hotel-bed-line text-forest text-lg"></i>
                </motion.div>
                <div>
                  <p className="font-sans-jp text-earth/70 text-xs mb-0.5">ベッド数</p>
                  <p className="font-sans-jp text-deep-brown text-sm">{salonInfo.beds}台（完全個室）</p>
                </div>
              </motion.div>
            </Reveal>
          </div>

          {/* Map */}
          <div className="lg:col-span-3">
            <Reveal className="overflow-hidden rounded-lg bg-linen" variants={fadeIn}>
              <div className="w-full h-72 sm:h-80 lg:h-[420px]">
                <iframe
                  title="ほぐし屋さん Luminosite 地図"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3280.236123456789!2d135.5114!3d34.7044!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDQyJzE1LjgiTiAxMzXCsDMwJzQxLjAiRQ!5e0!3m2!1sja!2sjp!4v1620000000000!5m2!1sja!2sjp"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}